import React, {useEffect, useCallback, useState} from 'react';
import {
  DataGrid,
  type GridColDef,
  type DataGridProps,
  useGridApiContext,
  type GridEventListener,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import clsx from 'clsx';
import './ArrDataGrid.scss';
import {ReactComponent as SortIcon} from '../../../assets/images/sort.svg';
import {ReactComponent as DescendingIcon} from '../../../assets/images/arrow-increase.svg';
import {ReactComponent as AscendingIcon} from '../../../assets/images/arrow-decrease.svg';
import {ReactComponent as FolderIcon} from '../../../assets/images/no-data-found.svg';
import {ReactElement, useMemo} from 'react';
import ArrPagination from '../arr-pagination/ArrPagination';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrLoadingSpinner from '../arr-loading-spinner/ArrLoadingSpinner';
import CellWrapper from '../arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';

export interface ArrDataGridProps extends DataGridProps {
  columns: GridColDef[];
  pageSize?: number;
  className?: string;
  currentPage?: number;
  setCurrentPage?: (arg0: number) => void;
  itemsPerPage?: number;
  setItemsPerPage?: (arg0: number) => void;
  onPageChange?: (pagenumber: number) => void;
  footerChildComponent?: ReactElement;
  entityType?: string;
  noRowsOverlay?: () => React.ReactElement;
  loadingOverlay?: () => React.ReactElement;
  primaryKey?: string;
  loading?: boolean;
  showPerPageOptions?: boolean;
  alwaysShowPagination?: boolean;
  disableColumnResize?: boolean;
  noRowsOverlayCustomText?: string;
}

const ArrDataGrid: React.FC<ArrDataGridProps> = ({
  rows,
  columns,
  className,
  currentPage = 1,
  setCurrentPage,
  itemsPerPage = 10,
  setItemsPerPage,
  onPageChange,
  footerChildComponent,
  entityType = '',
  primaryKey = 'id',
  rowCount = 0,
  noRowsOverlay,
  loadingOverlay,
  loading,
  showPerPageOptions,
  pageSizeOptions = [],
  alwaysShowPagination,
  disableColumnResize = true,
  noRowsOverlayCustomText,
  ...props
}) => {
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const totalItems = rowCount ? rowCount : rows?.length;

  const handleColumnWidthChange = useCallback<
    GridEventListener<'columnWidthChange'>
  >((params) => {
    const field = params.colDef.field;
    const width = params.width;
    setColumnWidths((prev) => {
      if (prev[field] === width) return prev;
      return {...prev, [field]: width};
    });
  }, []);

  const computedColumns = useMemo<GridColDef[]>(() => {
    return columns.map((column) => {
      const storedWidth = columnWidths[column.field];
      const finalWidth = storedWidth ?? column.width;
      const shouldRemoveFlex = finalWidth != null;
      let cellProps = {};
      if (!column.renderCell) {
        cellProps = {
          renderCell: (params: GridRenderCellParams) => {
            const rowIndex = params.api.getRowIndexRelativeToVisibleRows(
              params.id
            );
            return (
              <CellWrapper
                toolTipContent={params.value}
                cellParams={params}
                dataTestId={`cell-${column.headerName?.replaceAll(' ', '').toLowerCase()}-row-${rowIndex}`}
              >
                {params.value}
              </CellWrapper>
            );
          },
        };
      }
      return {
        ...column,
        minWidth: Math.min(column.minWidth ?? column.width ?? 96, 96),
        width: finalWidth,
        ...(shouldRemoveFlex ? {flex: undefined} : {}),
        headerClassName: clsx('typo-text label2_size', column.headerClassName),
        cellClassName: clsx('typo-text body3_size', column.cellClassName),
        tabIndex: 0,
        ...cellProps,
      };
    });
  }, [columns, columnWidths]);

  const CustomSortIcon = () => (
    <SortIcon className="arr-datagrid__sort-icon" data-testid="sort-icon" />
  );
  const CustomSortAscIcon = () => (
    <AscendingIcon
      className="arr-datagrid__sort-icon"
      data-testid="sort-asc-icon"
    />
  );
  const CustomSortDescIcon = () => (
    <DescendingIcon
      className="arr-datagrid__sort-icon"
      data-testid="sort-dsc-icon"
    />
  );

  const perPageOptions = useMemo(() => {
    const options = (pageSizeOptions as number[]).filter(
      (option) => option <= (totalItems || 0)
    );
    const additionalOption = (pageSizeOptions as number[]).find(
      (option) => option >= (totalItems || 0)
    );
    if (additionalOption && !options.includes(additionalOption)) {
      options.push(additionalOption);
    }
    return options;
  }, [pageSizeOptions, totalItems]);

  useEffect(() => {
    const interval = setInterval(() => {
      document
        .querySelectorAll('.MuiDataGrid-columnHeader')
        .forEach((el) => el.setAttribute('tabIndex', '-1'));
    }, 50);
    return () => clearInterval(interval);
  }, []);
  const CustomPagination: React.FC = () => {
    const apiRef = useGridApiContext();
    const handlePageChange = (page: number) => {
      apiRef.current.setPage(page - 1);
      setCurrentPage?.(page);
      onPageChange?.(page);
    };

    const setItemsPerPageHandler = (itemsPerPage: number) => {
      setItemsPerPage?.(itemsPerPage);
      apiRef.current.setPage(0);
      setCurrentPage?.(1);
      onPageChange?.(1);
      apiRef.current.setPageSize(itemsPerPage);
    };

    return rowCount > 0 ? (
      <div
        className="aur-datagrid-footer-child-container"
        data-testid={`arr-data-grid-div`}
        tabIndex={0}
      >
        {footerChildComponent ? footerChildComponent : null}
        <ArrPagination
          totalItems={rowCount}
          perPage={itemsPerPage}
          currentPage={currentPage}
          entityType={entityType}
          onPerPageChange={setItemsPerPageHandler}
          onPageChange={handlePageChange}
          showPerPageOptions={showPerPageOptions}
          perPageOptions={perPageOptions}
          alwaysShow={alwaysShowPagination}
        />
      </div>
    ) : (
      <div
        className="aur-datagrid-footer-child-container"
        data-testid={`arr-data-grid-div-empty-row`}
        tabIndex={0}
      >
        {footerChildComponent ? footerChildComponent : null}
      </div>
    );
  };

  const CustomLoadingOverlay: React.FC = () => {
    return (
      <Box className="loading-overlay" data-testid="grid-loading-overlay">
        <ArrLoadingSpinner show={loading} />
      </Box>
    );
  };

  const CustomNoRowsOverlay: React.FC = () => {
    return (
      <Box className="no-grid-overlay">
        <FolderIcon />
        <Typography
          className="typo-text headline2_size"
          data-testid={`arr-data-grid-no-rows`}
          tabIndex={0}
        >
          {noRowsOverlayCustomText ??
            t('common.arrDataGrid.noResults', {entity: entityType})}
        </Typography>
      </Box>
    );
  };

  return (
    <div
      className={clsx('arr-datagrid', className)}
      data-testid={`arr-data-grid-div-wrapper-${className}`}
      tabIndex={0}
    >
      <DataGrid
        rows={rows}
        disableRowSelectionOnClick
        disableColumnSorting={rows?.length === 0}
        disableColumnMenu={true}
        disableColumnSelector={true}
        disableColumnResize={disableColumnResize}
        onColumnWidthChange={handleColumnWidthChange}
        columns={computedColumns}
        slots={{
          columnUnsortedIcon: CustomSortIcon,
          columnSortedAscendingIcon: CustomSortAscIcon,
          columnSortedDescendingIcon: CustomSortDescIcon,
          noRowsOverlay: noRowsOverlay ?? CustomNoRowsOverlay,
          pagination: CustomPagination,
          loadingOverlay: loadingOverlay ?? CustomLoadingOverlay,
        }}
        getRowId={(row) => row[primaryKey]}
        loading={loading}
        {...props}
      />
    </div>
  );
};

export default ArrDataGrid;
